import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { FeedEntry } from './feed';

function ago(ms: number): string {
  const s = Math.max(0, Math.round(ms / 1000));
  if (s < 10) return 'now';
  if (s < 60) return `${s}s`;
  const m = Math.round(s / 60);
  return m < 60 ? `${m}m` : `${Math.round(m / 60)}h`;
}

const TAG_CLASS: Record<FeedEntry['tag'], string> = {
  E: 'border-ink bg-ink text-surface-1',
  PM: 'border-ink bg-surface-1 text-ink',
  OK: 'border-ok bg-ok text-surface-1',
};

const ROW = 'flex items-baseline gap-1.5 rounded-control px-1 py-0.5 hover:bg-surface-2';

/** Small real-time lines — what the agents are doing right now. */
export function LiveFeed({ entries }: { entries: FeedEntry[] }) {
  // Re-render every 15 s so relative ages stay fresh.
  const [clock, setClock] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setClock(Date.now()), 15_000);
    return () => clearInterval(t);
  }, []);

  if (entries.length === 0) {
    return <p className="rounded-card border border-line bg-surface-2 px-3 py-4 text-center text-xs text-ink-muted">No agent activity yet.</p>;
  }
  return (
    <ol aria-label="Live feed" aria-live="polite" aria-relevant="additions" className="space-y-0.5">
      {entries.map((e) => {
        const body: ReactNode = (
          <>
            <span className={`shrink-0 rounded-tag border px-1 font-display text-[9px] font-bold uppercase ${TAG_CLASS[e.tag]}`}>
              {e.tag === 'OK' ? '✓' : e.tag}
            </span>
            {e.code && <span className="shrink-0 font-mono text-2xs text-ink">{e.code}</span>}
            <span className="min-w-0 flex-1 truncate font-mono text-2xs text-ink-muted" title={e.text}>{e.text}</span>
            <time aria-hidden="true" className="shrink-0 font-mono text-2xs text-ink-faint">{ago(clock - e.at)}</time>
          </>
        );
        return (
          <li key={e.id} className="animate-fade-in motion-reduce:animate-none">
            {e.href === null ? (
              <div className={ROW}>{body}</div>
            ) : /^https?:\/\//.test(e.href) ? (
              <a href={e.href} target="_blank" rel="noopener noreferrer" className={ROW}>{body}</a>
            ) : (
              <Link to={e.href} className={ROW}>{body}</Link>
            )}
          </li>
        );
      })}
    </ol>
  );
}
